import { MgState, AnswerEntityInterface, AnswerQueryInterface, UserEntityInterface } from '@types';
import { createSelector } from 'reselect';
import { convertToRows } from '@components/Pages/AnswersPage/utils';
import _isEqual from 'lodash/isEqual';
import { currentUserSelector, isUserFromBYOrganization } from '@selectors/user';
import { currentQuestionnaireDataSelector } from '@selectors/questionnaire';
import { notAppliedAnswersFilters } from '@constants/answers';

const getAnswers = (store: MgState): AnswerEntityInterface[] => store?.answer?.answers;

export const currentAnswerSelector = (store: MgState): AnswerEntityInterface =>
  store?.answer?.currentAnswer;

export const currentAnswerOwnerSelector = (store: MgState): UserEntityInterface =>
  store?.answer?.currentAnswer?.owner;

export const answerTotalSelector = (store: MgState): number => store?.answer?.total;

export const checkedAnswersIdsSelector = (store: MgState): string[] =>
  store?.answer?.checkedAnswersIds;

export const answersFiltersSelector = (store: MgState): AnswerQueryInterface =>
  store?.answersPage?.filters;

export const answersRowsSelector = createSelector([getAnswers], (answers) =>
  convertToRows(answers)
);

export const answersPageNumberSelector = createSelector([answersFiltersSelector], (filters) => {
  return filters?.offset / filters?.limit;
});

export const answersRowsPerPageSelector = createSelector(
  [answersFiltersSelector],
  (filters) => filters?.limit
);

export const answersTotalPagesSelector = createSelector(
  [answersFiltersSelector, answerTotalSelector],
  (filters, total) => Math.ceil(total / filters?.limit)
);

export const checkedAnswersSelector = createSelector(
  [getAnswers, checkedAnswersIdsSelector],
  (answers, checkedAnswersIds) => {
    return checkedAnswersIds.map((findId) => answers.find(({ id }) => id === findId));
  }
);

export const isFilterAppliedSelectorFactory = (keys: (keyof AnswerQueryInterface)[]) =>
  createSelector([answersFiltersSelector], (filters) =>
    keys.every((key) => _isEqual(filters[key], notAppliedAnswersFilters[key]))
  );

export const answerEditModeSelector = (state: MgState) => state.answer.editMode;

export const createAnswerLoadingSelector = (state: MgState) => state.answer.isCreateAnswerLoading;

export const editingAnswerLoadingSelector = (state: MgState) => state.answer.isEditingAnswerLoading;

export const currentAnswerLoadingSelector = (state: MgState) => state.answer.isCurrentAnswerLoading;

export const navigateAnswerIdSelector = (state: MgState) => state.answer.navigateId;

export const currentAnswerReport = createSelector(
  [currentAnswerSelector],
  (currentAnswer) => currentAnswer?.report
);

export const isOwnAnswerSelector = createSelector(
  [currentAnswerOwnerSelector, currentUserSelector],
  (currentAnswerOwner, currentUser) => currentAnswerOwner?.id === currentUser?.id
);

export const showAgreementLinkSelector = createSelector(
  [
    currentAnswerOwnerSelector,
    currentAnswerReport,
    currentUserSelector,
    currentQuestionnaireDataSelector,
    isUserFromBYOrganization,
    answerEditModeSelector,
  ],
  (
    currentAnswerOwner,
    currentAnswerReport,
    currentUser,
    currentQuestionnaireData,
    isBYUser,
    editMode
  ) =>
    (currentAnswerOwner?.id === currentUser?.id && Boolean(currentAnswerReport)) ||
    (currentQuestionnaireData?.hasAgreement && isBYUser && !editMode)
);

export const currentAnswerLanguageSelector = createSelector(
  [currentAnswerSelector],
  (currentAnswer) => currentAnswer?.questionnaire.lang
);
